import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  IconButton,
  List,
  Switch,
  Typography,
} from '@shared/ui';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';

type DriveItem = { id: string; name: string };

interface DriveDetailProps {
  back: () => void;
  iconSrc?: string;
  isLoading: boolean;
  onRefresh: () => void;
  visibleFolders: DriveItem[];
  visibleFiles: DriveItem[];
  drivePickerOpen: null | 'folders' | 'files';
  folders: DriveItem[];
  files: DriveItem[];
  tempSelection: string[];
  setTempSelection: Dispatch<SetStateAction<string[]>>;
  openFoldersPicker: () => void;
  openFilesPicker: () => void;
  closePicker: () => void;
  applyPicker: () => void;
}

const DriveDetail: React.FC<DriveDetailProps> = ({
  back,
  iconSrc,
  isLoading,
  onRefresh,
  visibleFolders,
  visibleFiles,
  drivePickerOpen,
  folders,
  files,
  tempSelection,
  setTempSelection,
  openFoldersPicker,
  openFilesPicker,
  closePicker,
  applyPicker,
}) => {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <IconButton size="small" onClick={back} aria-label="Back">
          <Icon>arrow_back</Icon>
        </IconButton>
        <Avatar
          variant="rounded"
          sx={{ width: 28, height: 28, ml: 0.5, mr: 1, borderRadius: 6, bgcolor: 'transparent' }}
          src={iconSrc}
        >
          <Icon sx={{ fontSize: 18 }}>drive_folder_upload</Icon>
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Google Drive
          </Typography>
        </Box>
        <IconButton size="small" onClick={onRefresh} aria-label="Refresh">
          <Icon>refresh</Icon>
        </IconButton>
      </Box>
      <Divider sx={{ my: 1 }} />

      {/* Global sharing toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Create insights from any team-shared files
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Moments will generate insights from any file shared with one of your team members
          </Typography>
        </Box>
        <Switch size="small" />
      </Box>
      <Divider sx={{ my: 1 }} />

      {/* Directories section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
          Directories
        </Typography>
        <Button size="small" variant="outlined" onClick={openFoldersPicker} disabled={isLoading}>
          Add
        </Button>
      </Box>
      <List sx={{ p: 0 }}>
        {isLoading && (
          <Box sx={{ px: 1, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
        {!isLoading && visibleFolders.length === 0 && (
          <Box sx={{ px: 1, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              No folders selected yet.
            </Typography>
          </Box>
        )}
        {visibleFolders.map((f) => (
          <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75 }}>
            <Icon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }}>folder</Icon>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {f.name}
            </Typography>
            <AvatarGroup
              max={3}
              sx={{ '& .MuiAvatar-root': { width: 20, height: 20, fontSize: 10 } }}
            >
              <Avatar sx={{ width: 20, height: 20 }}>D</Avatar>
              <Avatar sx={{ width: 20, height: 20 }}>A</Avatar>
              <Avatar sx={{ width: 20, height: 20 }}>C</Avatar>
            </AvatarGroup>
            <IconButton size="small" sx={{ ml: 0.5 }}>
              <Icon>more_horiz</Icon>
            </IconButton>
          </Box>
        ))}
      </List>

      {/* Files section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600 }}>
          Files
        </Typography>
        <Button size="small" variant="outlined" onClick={openFilesPicker} disabled={isLoading}>
          Add
        </Button>
      </Box>
      <List sx={{ p: 0 }}>
        {isLoading && (
          <Box sx={{ px: 1, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        )}
        {!isLoading && visibleFiles.length === 0 && (
          <Box sx={{ px: 1, py: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              No files selected yet.
            </Typography>
          </Box>
        )}
        {visibleFiles.map((f) => (
          <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75 }}>
            <Icon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }}>description</Icon>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {f.name}
            </Typography>
            <AvatarGroup
              max={3}
              sx={{ '& .MuiAvatar-root': { width: 20, height: 20, fontSize: 10 } }}
            >
              <Avatar sx={{ width: 20, height: 20 }}>D</Avatar>
              <Avatar sx={{ width: 20, height: 20 }}>A</Avatar>
              <Avatar sx={{ width: 20, height: 20 }}>C</Avatar>
            </AvatarGroup>
            <IconButton size="small" sx={{ ml: 0.5 }}>
              <Icon>more_horiz</Icon>
            </IconButton>
          </Box>
        ))}
      </List>

      {/* Picker dialog */}
      <Dialog open={!!drivePickerOpen} onClose={closePicker} fullWidth maxWidth="sm">
        <DialogTitle>
          {drivePickerOpen === 'folders' ? 'Select Directories' : 'Select Files'}
        </DialogTitle>
        <DialogContent>
          <List sx={{ p: 0 }}>
            {(drivePickerOpen === 'folders' ? folders : files).map((it) => (
              <Box key={it.id} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                <Checkbox
                  checked={tempSelection.includes(it.id)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const checked = e.target.checked;
                    setTempSelection((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(it.id);
                      else next.delete(it.id);
                      return Array.from(next);
                    });
                  }}
                />
                <Typography variant="body2">{it.name}</Typography>
              </Box>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePicker}>Cancel</Button>
          <Button variant="contained" onClick={applyPicker}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriveDetail;
